import moment from 'moment';
import React, { useMemo } from 'react';

import EventNoteIcon from '@mui/icons-material/EventNote';

import {
  DayOfWeek,
  MemberContactMethodType,
  MemberContactTimeType,
} from '@vestahealthcare/common/enums';
import { EMPTY, TIME_FORMAT } from '@vestahealthcare/common/utils/constants';

import { Tooltip } from 'styleguide-v2';

import { MemberContactInfoParsedResponse } from 'dash/src/services/PatientServices';

type Props = {
  contactInfo?: MemberContactInfoParsedResponse;
};

const SORT_TIME_ORDER = [
  undefined,
  MemberContactTimeType.ANY_TIME,
  MemberContactTimeType.MORNING,
  MemberContactTimeType.AFTERNOON,
  MemberContactTimeType.CUSTOM,
];

export const MemberContactInfoDetails = ({ contactInfo }: Props) => {
  const { contactMethods, contactDateTimes: contactTimes, note } =
    contactInfo ?? {};

  const sortedMethods = useMemo(
    () =>
      contactMethods
        ?.map(({ contactMethod, contactMethodOther }) =>
          contactMethod === MemberContactMethodType.OTHER
            ? `${contactMethod.toString()} - ${contactMethodOther}`
            : contactMethod.toString(),
        )
        .sort((a, b) => a.localeCompare(b))
        .join(', '),
    [contactMethods],
  );

  const sortedTimes = useMemo(
    () =>
      contactTimes?.toSorted(
        (a, b) =>
          SORT_TIME_ORDER.indexOf(a?.type) - SORT_TIME_ORDER.indexOf(b?.type),
      ) ?? [],
    [contactTimes],
  );

  if (!contactInfo) return <>{EMPTY}</>;

  return (
    <>
      {!!contactMethods?.length && <p className="no-margin">{sortedMethods}</p>}
      {!!contactTimes?.length &&
        sortedTimes.map(({ dates, type, startTime, endTime }, idx) => (
          <p className="no-margin" key={`member-contact-info-days-${idx}`}>
            {dates?.length > 0 && <>{DayOfWeek.stringify(dates)}:&nbsp;</>}
            {type !== MemberContactTimeType.CUSTOM && type?.toString()}
            {type === MemberContactTimeType.CUSTOM && (
              <>
                {startTime
                  ? moment(startTime.getTime()).format(TIME_FORMAT)
                  : '*'}
                &nbsp;-&nbsp;
                {endTime ? moment(endTime.getTime()).format(TIME_FORMAT) : '*'}
              </>
            )}
          </p>
        ))}
      {note && (
        <Tooltip text={note}>
          <EventNoteIcon
            color="primary"
            fontSize="small"
            sx={{ marginTop: '0.5rem' }}
          />
        </Tooltip>
      )}
      {!contactMethods?.length &&
        !contactTimes?.[0]?.dates &&
        !contactTimes?.[0]?.type &&
        !note &&
        EMPTY}
    </>
  );
};
